import { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Form, Button, InputGroup } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { resetPassword } from '../../modules/auth/_requests';

const initialValues = {
  password: '',
  passwordConfirmation: '',
};

const ResetForm = () => {
  const { t } = useTranslation(['auth'], { keyPrefix: 'form' });
  const { t: b } = useTranslation(['auth'], { keyPrefix: 'button' });
  const [viewPassword, setViewPassword] = useState(false);
  const [viewConfirmation, setViewConfirmation] = useState(false);
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const token = searchParams.get('token');

  const passwordValidationSchema = Yup.string()
    .min(8, t('password.min'))
    .max(16, t('password.max'))
    .matches(/[a-zA-Z]/, t('password.alphabetic'))
    .matches(/[0-9]/, t('password.numeric'));

  const resetPasswordSchema = Yup.object().shape({
    password: passwordValidationSchema.required(t('password.required')),
    passwordConfirmation: passwordValidationSchema
      .required(t('confirmation.required'))
      .oneOf([Yup.ref('password')], t('confirmation.match')),
  });

  const formik = useFormik({
    initialValues,
    validationSchema: resetPasswordSchema,
    onSubmit: async (values, { setSubmitting }) => {
      console.log(token);
      if (!token) return;

      resetPassword(values.password, token)
        .then(() => {
          setTimeout(navigate, 5000, '/login');
        })
        .catch(() => {
          setSubmitting(false);
          toast.error(t('failed'));
        });
    },
  });

  return (
    <Form
      noValidate
      onSubmit={formik.handleSubmit}
      style={{ minWidth: '300px' }}
    >
      <Form.Group controlId="formBasicPassword" className="mb-3">
        <Form.Label>{t('password.label')}</Form.Label>
        <InputGroup>
          <Form.Control
            type={viewPassword ? 'text' : 'password'}
            placeholder={t('password.placeholder')}
            {...formik.getFieldProps('password')}
            isInvalid={
              formik.touched.password && formik.errors.password !== undefined
            }
          />
          <Button
            variant="outline-secondary"
            type="button"
            onClick={() => setViewPassword(!viewPassword)}
            aria-label={t(viewPassword ? 'password.hide' : 'password.show')}
          >
            <i className={`bi bi-${viewPassword ? 'eye' : 'eye-slash'}`}></i>
          </Button>
        </InputGroup>
        <Form.Control.Feedback type="invalid">
          {formik.errors.password}
        </Form.Control.Feedback>
      </Form.Group>

      <Form.Group controlId="formBasicConf" className="mb-3">
        <Form.Label>{t('confirmation.label')}</Form.Label>
        <InputGroup>
          <Form.Control
            type={viewConfirmation ? 'text' : 'password'}
            placeholder={t('password.placeholder')}
            {...formik.getFieldProps('passwordConfirmation')}
            isInvalid={
              formik.touched.passwordConfirmation &&
              formik.errors.passwordConfirmation !== undefined
            }
          />
          <Button
            variant="outline-secondary"
            type="button"
            onClick={() => setViewConfirmation(!viewConfirmation)}
            aria-label={t(viewConfirmation ? 'password.hide' : 'password.show')}
          >
            <i
              className={`bi bi-${viewConfirmation ? 'eye' : 'eye-slash'}`}
            ></i>
          </Button>
        </InputGroup>
        <Form.Control.Feedback type="invalid">
          {formik.errors.passwordConfirmation}
        </Form.Control.Feedback>
      </Form.Group>
      <div className="d-flex justify-content-end gap-2">
        <Button
          variant="outline-dark"
          onClick={() => navigate('/login')}
          className="text-end"
          disabled={formik.isSubmitting}
        >
          {b('cancel')}
        </Button>
        <Button
          variant="outline-dark"
          type="submit"
          className="text-end"
          disabled={formik.isSubmitting || !formik.isValid}
        >
          {b(formik.isSubmitting ? 'wait' : 'submit')}
        </Button>
      </div>
    </Form>
  );
};

export default ResetForm;
